// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { RadioStyle } from "@insite/mobius/Radio";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { subscribeStyles } from "@rpsc/Overrides/Widgets/Basic/Subscribe";
import { fontConfig } from "@rpsc/XDL/PreStyle/typography";
import { color, getSystemColor, xScreen } from "@rpsc/XDL/Utilities";
import { css } from "styled-components";

subscribeStyles.mainContainer = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 14px;
        width: 882px;
        background-color: ${color("primary")};
        padding: 16px 24px;
        margin: 0 auto;
        border-radius: 2px;
        transition: height 400ms;
        ${xScreen("lte", "md")} {
            width: 100%;
        }
        ${xScreen("lte", "sm")} {
            gap: 16px;
        }
    `,
};

subscribeStyles.topSectionWrapper = {
    css: css`
        display: flex;
        gap: 20px;
        ${xScreen("lte", "sm")} {
            flex-direction: column;
            gap: 12px;
        }
    `,
};

subscribeStyles.infoWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 8px;
        max-width: 365px;
        ${xScreen("lte", "sm")} {
            max-width: 100%;
        }
    `,
};

subscribeStyles.titleLabel = {
    variant: "h4",
    forwardAs: "p",
    color: "common.ui1",
    transform: "capitalize",
    css: css`
        margin-bottom: 0;
    `,
};

subscribeStyles.descriptionText = subscribeStyles.disclaimerText = {
    color: "common.ui1",
    weight: fontConfig.weight.regular,
    css: css`
        ${TypographyStyle} {
            font-size: 12px;
            line-height: 17px;
            margin-bottom: 0;
        }
    `,
};

subscribeStyles.emailWrapper = {
    css: css`
        position: relative;
        margin-top: 8px;
        flex: 1;
        ${xScreen("lte", "sm")} {
            margin-top: 0;
        }
    `,
};

subscribeStyles.emailTextField = {
    cssOverrides: {
        formField: css`
            width: 100%;
        `,
        inputSelect: css`
            &,
            &:focus {
                padding-right: 100px;
            }
            ${(props: any) => {
                if (props.error) {
                    return `
                    &:not(:disabled) {
                        border-color: ${getSystemColor("danger.secondary")};
                    }
                `;
                }
            }}
        `,
        descriptionWrapper: css`
            ${TypographyStyle} {
                color: ${color("danger.secondary")};
                font-size: 12px;
                line-height: 17px;
            }
        `,
    },
};

subscribeStyles.emailButton = {
    variant: "secondary",
    css: css`
        width: 94px;
        min-height: 28px;
        position: absolute;
        right: 4px;
        top: 3px;
    `,
    typographyProps: {
        size: 11,
        lineHeight: "15px",
    },
};

subscribeStyles.emailsRadioGroup = {
    labelProps: {
        variant: "h6",
        color: "common.ui1",
        size: 14,
    },
    css: css`
        & > ${RadioStyle} ${TypographyStyle} {
            font-size: 12px;
            line-height: 17px;
            font-weight: ${fontConfig.weight.regular};
            color: ${color("common.ui1")};
        }
    `,
};

subscribeStyles.recaptchaWrapper = {
    css: css`
        display: flex;
        justify-content: center;
    `,
};

subscribeStyles.recaptchaStyle = {
    errorMessageText: {
        color: "danger.secondary",
        css: css`
            margin-top: 8px;
            font-size: ${fontConfig.fontSize.xSmall};
            line-height: ${fontConfig.lineHeight.xSmall};
            font-weight: ${fontConfig.weight.regular};
        `
    }
}